import { Link } from 'react-router-dom';
import { formatMoneyCompact } from '../../shared/money';
import { getReferencePackLicenseLabel } from '../legal/referencePackLicenseLabels';

export interface ReferencePackCardData {
  title: string;
  description: string | null;
  priceCents: number | null;
  currency: string | null;
  coverImageUrl: string | null;
  photoCount: number;
  videoCount: number;
  license: string | null;
  accessDurationLabel?: string | null;
  accessExpiresOn: string | null;
}

interface ReferencePackCardProps {
  pack: ReferencePackCardData;
  href?: string;
  onClick?: () => void;
  priceLabelOverride?: string;
}

export function getLicenseLabel(license: string | null | undefined): string | null {
  return getReferencePackLicenseLabel(license);
}

export function getReferencePackAccessDurationLabel(
  count: string | number | null | undefined,
  unit: 'days' | 'weeks' | 'months' | 'years' | null | undefined
): string | null {
  const numericCount = Number(count);
  if (!Number.isFinite(numericCount) || numericCount <= 0 || !unit) return null;
  const singularUnit = unit.replace(/s$/, '');
  return `${numericCount} ${numericCount === 1 ? singularUnit : unit}`;
}

function formatPriceLabel(priceCents: number | null, currency: string | null): string {
  if (priceCents === null || priceCents <= 0) return 'Free';
  return formatMoneyCompact(priceCents, currency);
}

export function ReferencePackCard({ pack, href, onClick, priceLabelOverride }: ReferencePackCardProps) {
  const priceLabel = priceLabelOverride ?? formatPriceLabel(pack.priceCents, pack.currency);
  const licenseLabel = getLicenseLabel(pack.license);
  const description = pack.description?.trim();
  const accessDurationLabel = pack.accessDurationLabel?.trim() || null;
  const cardBody = (
    <>
      {pack.coverImageUrl ? (
        <div className="aspect-[4/3] bg-black">
          <img src={pack.coverImageUrl} alt="" className="h-full w-full object-cover" />
        </div>
      ) : (
        <div className="aspect-[4/3] bg-bg-secondary flex items-center justify-center px-6 text-center text-sm text-text-dim">
          {pack.title || 'Untitled pack'}
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h2 className="min-w-0 flex-1 font-display text-[22px] leading-tight text-text">
            {pack.title || 'Untitled pack'}
          </h2>
          <button type="button" tabIndex={-1} className="btn btn-accent h-11 shrink-0 px-5 text-sm cursor-default pointer-events-none">
            {priceLabel}
          </button>
        </div>
        <p className="mt-1 text-xs font-medium text-text-dim">
          {pack.videoCount} video{pack.videoCount === 1 ? '' : 's'} · {pack.photoCount} photo{pack.photoCount === 1 ? '' : 's'}
        </p>
        {description && (
          <p className="mt-2 line-clamp-3 text-sm text-text-dim leading-relaxed">{description}</p>
        )}
        {(licenseLabel || accessDurationLabel || pack.accessExpiresOn) && (
          <div className="mt-3 border-t border-line-soft pt-3 text-[11px] leading-relaxed text-text-faint">
            {licenseLabel && <div>{licenseLabel}</div>}
            {accessDurationLabel && <div>Access for {accessDurationLabel} from purchase</div>}
            {pack.accessExpiresOn && <div>Access until {pack.accessExpiresOn}</div>}
          </div>
        )}
      </div>
    </>
  );

  const shellClass = 'group block bg-panel border border-line rounded-xl overflow-hidden shadow-sm transition-colors hover:border-text';

  if (href) {
    return (
      <Link to={href} className={shellClass}>
        {cardBody}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${shellClass} text-left w-full`}>
        {cardBody}
      </button>
    );
  }

  return <article className={shellClass}>{cardBody}</article>;
}
